import React, { useEffect, useMemo, useState } from 'react'
import './CallFlowStyle.css'
import SettingsSkeleton from '@pages/Settings/SettingsSkeleton.page'
import useCallFlow from './useCallFlow'
import { ICallFlow } from '@dataTypes/clinic.type'
import { formatPhoneNumber } from '@utils/numberFormatter.util'
import CallFlowEditor from './FlowEditor/FlowEditor.component'
import CallFlowDeleteModal from './CallFlowDeleteModal.component'
import SelectFlowTypeModal from './SelectFlowTypeModal.component'
import { flowTypeLabel } from '@utils/communicationFlow.util'
import {
  ActionButtons,
  Badge,
  Button,
  DataTable,
  EmptyState,
  Modal,
  SearchWithFilter,
  ToggleSwitch,
  type DataTableColumn,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import { Icon as AlertIcon } from '@enamelai/design-system/icons/Alert'
import { Icon as CallIcon } from '@enamelai/design-system/icons/Call'
import { Icon as InfoIcon } from '@enamelai/design-system/icons/Info'

type CallFlowRow = { id: string }

type CallFlowSortColumn = 'status' | 'name' | 'numbers' | 'flow_type'

const DEFAULT_PAGE_SIZE = 10

const textSortValue = (value?: string | null): string | null => {
  const trimmed = value?.trim()
  return trimmed ? trimmed.toLowerCase() : null
}

const compareSortValues = (
  aValue: string | null,
  bValue: string | null,
  direction: DataTableSortDirection,
): number => {
  const multiplier = direction === 'asc' ? 1 : -1
  if (aValue === bValue) return 0
  if (aValue === null) return 1
  if (bValue === null) return -1
  return aValue < bValue ? -multiplier : multiplier
}

const flowRowId = (flow: ICallFlow, index: number): string =>
  flow.call_flow_id ?? `call-flow-${index}`

const formatFlowNumbers = (flow: ICallFlow): string => {
  const links = flow.number_links ?? []
  if (links.length === 0) return ''
  return links
    .map((link) =>
      link.clinic_number?.phone_number
        ? formatPhoneNumber(link.clinic_number.phone_number)
        : link.clinic_number_id,
    )
    .join(', ')
}

const CallFlow = (): React.ReactElement => {
  const hooks = useCallFlow()
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sortColumnId, setSortColumnId] = useState<CallFlowSortColumn | null>(
    null,
  )
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')

  useEffect(() => {
    if (hooks.mode === 'list') {
      hooks.setHeaderTitle('Communication flow')
      hooks.setHeaderButtons(null)
    }
    return () => {
      hooks.setHeaderTitle(null)
      hooks.setHeaderButtons(null)
    }
  }, [hooks.mode, hooks.setHeaderButtons, hooks.setHeaderTitle])

  useEffect(() => {
    setCurrentPage(1)
  }, [hooks.mainSearchQuery, hooks.filters])

  const flowsById = useMemo(() => {
    const map = new Map<string, ICallFlow>()
    hooks.filteredCallFlows.forEach((flow, index) => {
      map.set(flowRowId(flow, index), flow)
    })
    return map
  }, [hooks.filteredCallFlows])

  const renderFlow =
    (render: (flow: ICallFlow) => React.ReactNode) =>
    (row: CallFlowRow): React.ReactNode => {
      const flow = flowsById.get(row.id)
      return flow ? render(flow) : null
    }

  const columns: DataTableColumn<CallFlowRow>[] = [
    {
      id: 'status',
      label: 'Status',
      width: '8%',
      renderCell: renderFlow((data) => {
        const isDraft = data.status === 'draft'
        const isActive = data.status === 'active'
        return (
          <span
            className="scp-call-flow-status-wrap"
            data-bs-toggle={isDraft ? 'tooltip' : undefined}
            title={
              isDraft
                ? 'This flow is still in draft mode. Complete the missing flow to activate.'
                : undefined
            }
          >
            <ToggleSwitch
              key={`${data.call_flow_id}-${isActive}`}
              checked={isActive}
              onChange={() => {
                hooks.setSwitchFlowConfirm(data)
              }}
              aria-label={`${data.name} status`}
              disabled={isDraft}
            />
          </span>
        )
      }),
    },
    {
      id: 'name',
      label: 'Flow name',
      width: '16%',
      sortable: true,
      renderCell: renderFlow((data) => data.name),
    },
    {
      id: 'numbers',
      label: 'Phone number(s)',
      width: '46%',
      sortable: true,
      renderCell: renderFlow((data) => formatFlowNumbers(data) || '—'),
    },
    {
      id: 'flow_type',
      label: 'Flow type',
      width: '20%',
      sortable: true,
      renderCell: renderFlow((data) => {
        const sms = data.flow_type === 'sms'
        return (
          <Badge
            mode="tinted"
            color={sms ? 'cyan' : 'blue'}
            label={flowTypeLabel(data.flow_type)}
          />
        )
      }),
    },
  ]

  const handleSort = (columnId: string): void => {
    const column = columnId as CallFlowSortColumn
    if (sortColumnId !== column) {
      setSortColumnId(column)
      setSortDirection('asc')
    } else if (sortDirection === 'asc') {
      setSortDirection('desc')
    } else {
      setSortColumnId(null)
    }
    setCurrentPage(1)
  }

  const sortedFlows = useMemo(() => {
    const entries = hooks.filteredCallFlows.map((flow, index) => ({
      flow,
      id: flowRowId(flow, index),
    }))
    if (!sortColumnId) return entries
    return [...entries].sort((left, right) => {
      const sortValue = (flow: ICallFlow): string | null => {
        if (sortColumnId === 'status') return textSortValue(flow.status)
        if (sortColumnId === 'name') return textSortValue(flow.name)
        if (sortColumnId === 'flow_type')
          return textSortValue(flowTypeLabel(flow.flow_type))
        return textSortValue(formatFlowNumbers(flow))
      }
      const leftValue = sortValue(left.flow)
      const rightValue = sortValue(right.flow)
      return compareSortValues(leftValue, rightValue, sortDirection)
    })
  }, [hooks.filteredCallFlows, sortColumnId, sortDirection])

  const totalPages = Math.max(1, Math.ceil(sortedFlows.length / pageSize))
  const pageNumber = Math.min(currentPage, totalPages)
  const pageRows = useMemo(
    (): CallFlowRow[] =>
      sortedFlows
        .slice((pageNumber - 1) * pageSize, pageNumber * pageSize)
        .map((entry) => ({ id: entry.id })),
    [sortedFlows, pageNumber, pageSize],
  )

  const activating = hooks.switchFlowConfirm?.status !== 'active'

  if (!hooks.dataReady) {
    return (
      <div className="scp-call-flow" id="settings-call-flow">
        <SettingsSkeleton variant="table" pageKey="comm-call-flow" />
      </div>
    )
  }

  return (
    <div className="scp-call-flow" id="settings-call-flow">
      {hooks.mode === 'list' ? (
        <div className="scp-call-flow-list">
          {!hooks.loading &&
          hooks.filteredCallFlows.length === 0 &&
          hooks.callFlows.length === 0 ? null : (
            <div className="scp-table-title-container">
              <span className="scp-table-title">
                Flow list
                <button
                  type="button"
                  className="scp-info-button ds-section-card__icon-button"
                  aria-label="Communication flow information"
                  title="Set how calls and texts are handled during work hours, breaks, or when closed."
                >
                  <InfoIcon aria-hidden="true" />
                </button>
              </span>
              <div className="component-buttons">
                <SearchWithFilter
                  placeholder="Search"
                  value={hooks.mainSearchQuery}
                  onChange={hooks.setMainSearchQuery}
                  filters={{ status: ['Draft', 'Active', 'Inactive'] }}
                  filterLabels={{ status: 'Status' }}
                  selectedFilters={hooks.filters}
                  onFilterChange={hooks.setFilters}
                />
                {hooks.callFlows.length > 0 ? (
                  <Button
                    size="sm"
                    variant="primary"
                    type="button"
                    onClick={() => hooks.setSelectFlowTypeOpen(true)}
                  >
                    Create flow
                  </Button>
                ) : null}
              </div>
            </div>
          )}
          {!hooks.dataReady ? null : hooks.filteredCallFlows.length === 0 ? (
            <EmptyState
              className="scp-call-flow-empty"
              illustration={<CallIcon className="empty-icon" aria-hidden />}
              title="No flows found"
              description={
                hooks.callFlows.length === 0
                  ? "You don't have any communication flows on record yet."
                  : 'No flows match your search.'
              }
              actionLabel={
                hooks.callFlows.length === 0 ? 'Create flow' : undefined
              }
              onAction={
                hooks.callFlows.length === 0
                  ? () => hooks.setSelectFlowTypeOpen(true)
                  : undefined
              }
            />
          ) : (
            <DataTable<CallFlowRow>
              className="scp-call-flow-table"
              rows={pageRows}
              columns={columns}
              actionsLabel="Actions"
              renderActions={renderFlow((data) => (
                <ActionButtons
                  onEdit={() => hooks.startEdit(data)}
                  onDelete={() => hooks.setDeleteFlowConfirm(data)}
                  disableDelete={hooks.loading}
                />
              ))}
              sortColumnId={sortColumnId ?? undefined}
              sortDirection={sortColumnId ? sortDirection : undefined}
              onSort={handleSort}
              pagination={{
                currentPage: pageNumber,
                totalPages,
                pageSize,
                onPageChange: setCurrentPage,
                onPageSizeChange: (size) => {
                  setPageSize(size)
                  setCurrentPage(1)
                },
              }}
            />
          )}
        </div>
      ) : (
        <CallFlowEditor
          initialFlow={hooks.draft}
          cancelEditor={hooks.cancelEditor}
        />
      )}

      <CallFlowDeleteModal
        show={hooks.deleteFlowConfirm !== null}
        callFlowName={hooks.deleteFlowConfirm?.name ?? ''}
        deleteDisabled={hooks.loading}
        onCancel={() => hooks.setDeleteFlowConfirm(null)}
        onDelete={() => {
          if (!hooks.deleteFlowConfirm) return
          void hooks.deleteFlow(hooks.deleteFlowConfirm)
        }}
      />
      <Modal
        open={hooks.switchFlowConfirm !== null}
        onClose={() => hooks.setSwitchFlowConfirm(null)}
        closeOnBackdrop={false}
        size="sm"
        variant="confirmation"
        icon={<AlertIcon width={16} height={16} aria-hidden="true" />}
        iconTone="warning"
        title={
          activating
            ? 'Are you sure you want to activate this?'
            : 'Are you sure you want to deactivate this?'
        }
        description={
          activating
            ? 'This flow will be activated. Are you sure you want to continue?'
            : 'This flow will be deactivated. Are you sure you want to continue?'
        }
        primaryLabel="Confirm"
        secondaryLabel="Back"
        onSecondaryClick={() => hooks.setSwitchFlowConfirm(null)}
        onPrimaryClick={() => {
          if (!hooks.switchFlowConfirm) return
          void hooks.updateCallFlowStatus(
            hooks.switchFlowConfirm,
            hooks.switchFlowConfirm.status === 'active' ? 'inactive' : 'active',
          )
        }}
      />
      <SelectFlowTypeModal
        open={hooks.selectFlowTypeOpen}
        onClose={() => hooks.setSelectFlowTypeOpen(false)}
        onSelect={hooks.startCreate}
      />
    </div>
  )
}

export default CallFlow
